// Lista de pedidos: todos los que hay, en cualquier estado, con lo justo para
// mirarlos y para deshacer uno.
//
// Existe porque anular vivia escondido en Cuentas, que es una pantalla de
// contabilidad: nadie iba a buscar ahi un pedido. Aca la lista es lo primero que
// se ve, el numero se toca para ver el resumen y el Anular esta al lado.
//
// No es la bandeja de "Pedidos web": esa muestra SOLO los PENDIENTE y sirve para
// confirmarlos o rechazarlos. Esta muestra todo, incluido lo ya confirmado y lo
// anulado, y es de donde se deshace un pedido que ya entro.

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { hoyMontevideo, rangoFechas } from "../../lib/fecha";
import { iconoTacho } from "../iconos";
import { abrirResumen } from "../pedidoresumen";
import { abrirAnular } from "../anular";
import { nombreTurno } from "../../lib/types";
import type { Pedido, EstadoPedido, Sesion } from "../../lib/types";

type PedidoFila = Pedido & { cliente_nombre: string; cliente_codigo: string };

const RECIENTES = 40;

const CHIP: Record<EstadoPedido, { texto: string; clase: string }> = {
  PENDIENTE: { texto: "Pendiente", clase: "bg-amber-100 text-amber-800" },
  CONFIRMADO: { texto: "Confirmado", clase: "bg-green-100 text-green-800" },
  ANULADO: { texto: "Anulado", clase: "bg-crema-200 text-masa-500" },
};

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}
function ddmmaaaa(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

export async function renderPedidos(contenedor: HTMLElement): Promise<void> {
  const hoy = hoyMontevideo();

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Pedidos</h1>
      <p class="subtitulo">Todos los pedidos, en cualquier estado. Tocá el número para ver el resumen.</p>
    </div>

    <div class="tarjeta mb-4 flex flex-wrap items-end gap-3">
      <label><span class="etiqueta">Estado</span>
        <select id="estado" class="campo">
          <option value="">Todos</option>
          <option value="PENDIENTE">Pendientes</option>
          <option value="CONFIRMADO">Confirmados</option>
          <option value="ANULADO">Anulados</option>
        </select></label>
      <label><span class="etiqueta">Entrega desde</span><input id="desde" type="date" class="campo" /></label>
      <label><span class="etiqueta">hasta</span><input id="hasta" type="date" class="campo" /></label>
      <div class="flex flex-wrap gap-1">
        <button class="btn-sec text-sm" data-rango="recientes">Recientes</button>
        <button class="btn-sec text-sm" data-rango="dia">Día</button>
        <button class="btn-sec text-sm" data-rango="semana">Semana</button>
        <button class="btn-sec text-sm" data-rango="mes">Mes</button>
      </div>
      <button id="permitirAnular" class="btn-sec hidden" title="Muestra el botón Anular en los pedidos confirmados"></button>
    </div>

    <div id="lista" class="space-y-3"><div class="tarjeta text-center text-masa-400">Cargando...</div></div>
    <dialog id="dlgResumen"></dialog>
    <dialog id="dlgAnular"></dialog>`;

  const selEstado = contenedor.querySelector("#estado") as HTMLSelectElement;
  const inpDesde = contenedor.querySelector("#desde") as HTMLInputElement;
  const inpHasta = contenedor.querySelector("#hasta") as HTMLInputElement;
  const lista = contenedor.querySelector("#lista") as HTMLElement;
  const btnPermitir = contenedor.querySelector("#permitirAnular") as HTMLButtonElement;
  const dlgResumen = contenedor.querySelector("#dlgResumen") as HTMLDialogElement;
  const dlgAnular = contenedor.querySelector("#dlgAnular") as HTMLDialogElement;

  // Anular pide su propio permiso: ver la lista y deshacer un pedido no son la
  // misma responsabilidad. Sin el permiso el boton no se dibuja, en vez de
  // aparecer y devolver 403 recien al tocarlo.
  let puedeAnular = false;

  /**
   * Los botones Anular arrancan ESCONDIDOS y hay que habilitarlos a mano, igual
   * que en el estado de cuenta.
   *
   * Aca la lista es larga y se lee scrolleando en el celular: el Anular queda a
   * la altura del pulgar, al lado del total, y un roce basta. Que haya que
   * encenderlos primero convierte el gesto en algo deliberado. Se vuelve a
   * apagar cada vez que se entra a la pantalla.
   */
  let anulacionesVisibles = false;
  let pedidos: PedidoFila[] = [];

  function pintarBotonPermitir(): void {
    btnPermitir.classList.toggle("hidden", !puedeAnular);
    btnPermitir.textContent = anulacionesVisibles
      ? "Anulaciones habilitadas"
      : "Habilitar anulaciones";
    btnPermitir.classList.toggle("btn-peligro", anulacionesVisibles);
    btnPermitir.classList.toggle("btn-sec", !anulacionesVisibles);
  }

  try {
    const s = await api.get<Sesion>("/api/sesion");
    puedeAnular = s.es_admin || s.permisos.includes("anular");
  } catch {
    /* sin permiso confirmado, no se ofrece anular */
  }
  pintarBotonPermitir();

  // Encender o apagar los botones NO vuelve a pedir la lista: se repinta la que
  // ya esta cargada.
  btnPermitir.addEventListener("click", () => {
    anulacionesVisibles = !anulacionesVisibles;
    pintarBotonPermitir();
    pintar();
  });

  async function cargar(): Promise<void> {
    const partes: string[] = [];
    if (selEstado.value) partes.push(`estado=${selEstado.value}`);
    // Sin fechas es el modo "Recientes": los ultimos, sin importar la entrega.
    if (inpDesde.value && inpHasta.value) {
      partes.push(`desde=${inpDesde.value}`, `hasta=${inpHasta.value}`);
    } else {
      partes.push(`limite=${RECIENTES}`);
    }

    lista.innerHTML = `<div class="tarjeta text-center text-masa-400">Cargando...</div>`;
    try {
      pedidos = await api.get<PedidoFila[]>(`/api/pedidos?${partes.join("&")}`);
    } catch (e) {
      pedidos = [];
      lista.innerHTML = `<div class="tarjeta text-red-700">${esc(
        e instanceof Error ? e.message : "Error",
      )}</div>`;
      return;
    }
    pintar();
  }

  function pintar(): void {
    if (pedidos.length === 0) {
      lista.innerHTML = `<div class="tarjeta text-center text-masa-400">No hay pedidos con ese filtro.</div>`;
      return;
    }

    lista.innerHTML = pedidos
      .map((p) => {
        const chip = CHIP[p.estado] ?? { texto: p.estado, clase: "bg-crema-200 text-masa-500" };
        return `
        <div class="tarjeta">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="min-w-0">
              <div class="text-lg font-bold">${esc(p.cliente_nombre)}</div>
              <div class="text-xs text-masa-400">
                ${esc(p.cliente_codigo)} ·
                <button type="button" data-resumen="${p.id}"
                        class="rounded px-1 font-semibold text-acento-700 underline underline-offset-2 hover:bg-acento-50"
                        title="Ver el resumen de este pedido">pedido #${esc(p.id)}</button>
                · entrega ${ddmmaaaa(p.fecha_entrega)}
              </div>
            </div>
            <span class="flex items-center gap-1">
              <span class="rounded-full bg-crema-200 px-2.5 py-1 text-xs font-bold text-masa-700">${esc(nombreTurno(p.turno))}</span>
              <span class="rounded-full px-2.5 py-1 text-xs font-bold ${chip.clase}">${esc(chip.texto)}</span>
            </span>
          </div>
          <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
            <span class="text-lg font-bold tabular-nums">${formatearPesos(p.total)}</span>
            ${
              puedeAnular && anulacionesVisibles && p.estado === "CONFIRMADO"
                ? `<button class="btn-peligro" data-anular="${p.id}">${iconoTacho} Anular</button>`
                : p.estado === "PENDIENTE"
                  ? `<span class="text-xs text-masa-400">Se confirma o se rechaza desde <b>Pedidos web</b></span>`
                  : ""
            }
          </div>
        </div>`;
      })
      .join("");

    // Los datos del cartel de anulacion salen de la fila ya cargada: no hace
    // falta otra vuelta al servidor para confirmar.
    lista.querySelectorAll<HTMLElement>("[data-anular]").forEach((b) => {
      b.addEventListener("click", () => {
        const p = pedidos.find((x) => x.id === Number(b.dataset.anular));
        if (!p) return;
        abrirAnular(
          dlgAnular,
          {
            id: p.id,
            cliente: p.cliente_nombre,
            fecha_entrega: p.fecha_entrega,
            turno: p.turno,
            total: p.total,
          },
          cargar,
        );
      });
    });
  }

  lista.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLElement>("[data-resumen]");
    if (b) void abrirResumen(dlgResumen, Number(b.dataset.resumen));
  });

  selEstado.addEventListener("change", () => void cargar());
  inpDesde.addEventListener("change", () => void cargar());
  inpHasta.addEventListener("change", () => void cargar());

  contenedor.querySelectorAll<HTMLButtonElement>("[data-rango]").forEach((b) => {
    b.addEventListener("click", () => {
      const tipo = b.dataset.rango as "recientes" | "dia" | "semana" | "mes";
      if (tipo === "recientes") {
        inpDesde.value = "";
        inpHasta.value = "";
      } else {
        const [d, h] = rangoFechas(inpHasta.value || hoy, tipo);
        inpDesde.value = d;
        inpHasta.value = h;
      }
      void cargar();
    });
  });

  await cargar();
}
